import {
    createContext,
    useContext,
    useEffect,
    useState,
  } from "react";
  import { type Session, type User } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useNavigate } from "react-router-dom";
  
  export interface AuthContextProps {
    user: User | null;
    session: Session | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
  };
  
  const AuthContext = createContext({});
  const supabase = useSupabaseClient();

  export { AuthContext };

  export function AuthProvider({...props }) {
    const [user, setUser] = useState<User | null | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState<Session | null>(null);
    const navigate = useNavigate();
  
    useEffect(() => {
      const init = async () => {
        const { data } = await supabase.auth.getUser();
    
        setUser(data.user);
        setLoading(false);
      };
    
      init();
    }, []);

    useEffect(() => {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event) => {
        if (event === "SIGNED_OUT") {
          navigate("/login");
        }
      });
    
      return () => {
        subscription.unsubscribe();
      };
    }, []);
    
    useEffect(() => {
      supabase.auth.getSession().then(({ data }) => {
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setLoading(false);
      });
  
      
      const { data: listener } = supabase.auth.onAuthStateChange(
        (_event, session) => {
          setSession(session);
          setUser(session?.user ?? null);
        }
      );
  
      return () => {
        listener.subscription.unsubscribe();
      };
    }, []);
  
    const signIn = async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    };
  
    const signUp = async (email: string, password: string) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
    };
  
    const signOut = async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    };
  
    return (
      <AuthContext.Provider
        {...props}
        value={{ user, session, loading, signIn, signOut, signUp }}
      />
    );
  }
  
  export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
      throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
  }